import { getWeek } from 'date-fns';
import { useTranslation } from 'next-i18next/pages';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo } from 'react';

import { Match } from '@pflegia/data-access';
import { CandidatePriority, INITIATIVE_INDEXED_PRIORITIES } from '@pflegia/types/api/candidate/candidate-priority';
import { JobPositionStatus } from '@pflegia/types/api/job-position/job-position-status';
import { ConnectionStatus } from '@pflegia/types/api/match/connection-status';
import { SourceDomain } from '@pflegia/types/api/source-domain/source-domain';
import { OfferType } from '@pflegia/types/common/enums/offer-type.enum';
import { useCanonicalUrl } from '@pflegia/ui/hooks/use-canonical-url';
import { useFlavorFromRoute } from '@pflegia/ui/hooks/use-flavor-from-route';
import { JOB_DETAILS } from '@pflegia/ui/pflegia/constants/routes';
import NextSeo from '@pflegia/ui/shared/next-seo';
import { getPflegiaDefaultDomainName } from '@pflegia/ui/shared/utils/get-pflegia-default-domain';
import { isDefined } from '@pflegia/util/common/type-guards';
import { getPublicImageUrl } from '@pflegia/util/functions/s3/get-public-image-url';

import getCanonicalForTransparentPages from '@domains/job-board/utils/get-canonical-for-transparent-pages';
import { JobDetailPageProps } from '@domains/job-detail/types/job-detail-page-props';

import { useLogoUrl } from '@hooks/use-logo-url';
import { useRouterHelper } from '@hooks/use-router-helper';

import { TrackingEventName } from '@shared/application/marketing/events/tracking-event-name';
import { useTracking } from '@shared/application/tracking.service';

const NUMBER_OF_ROTATING_TITLES = 3;

const JobDetailsSeoTags: React.FC<JobDetailPageProps & { match?: Match }> = ({ match, jobPosition, isAnonymized = true, asPath }) => {
  const { track } = useTracking();
  const logoUrl = useLogoUrl();
  const { sourceDomainName: appName } = useFlavorFromRoute();
  const { isJobDetailsPage, isJobRoute, isAnyJobPage, isPreview } = useRouterHelper();

  const {
    i18n: { language },
    t,
  } = useTranslation('jobDetails');

  useEffect(() => {
    if (match?.connectionStatus === ConnectionStatus.Pending && match?.offerType === OfferType.Push) {
      track(match?.isMachineLearningInvite ? TrackingEventName.ModelPushInviteOpen : TrackingEventName.RegularPushInviteOpen);
    }
  }, [match, track]);

  const descriptionVariables = { jobTitle: jobPosition.headline || jobPosition.title, employerName: jobPosition.employer.name };
  const description =
    jobPosition.metaDescription ??
    t(jobPosition.employer.formalLanguage ? 'seo.formal.description' : 'seo.description', descriptionVariables);
  const location = jobPosition.geoLocation?.city || t('seo.locationFallback', { defaultValue: 'Deutschland' });
  const rotatingTitleVariables = { jobTitle: descriptionVariables.jobTitle, location, appName };

  const rotatingTitleKey = getRotatingTitleKey();
  const title = t(rotatingTitleKey, rotatingTitleVariables);
  const route = useRouter();
  const routeAsPath = asPath || route.asPath;
  const canonicalUrl = useCanonicalUrl();

  const defaultDomain = getPflegiaDefaultDomainName(jobPosition.employer.sourceDomains as SourceDomain[]);

  const canonical = canonicalUrl({
    flavorDomain: defaultDomain,
    defaultDomain: SourceDomain.PflegiaDe,
    path: isAnyJobPage ? routeAsPath : getCanonicalForTransparentPages(routeAsPath, JOB_DETAILS),
    removeQueryParams: true,
  });

  const isNoIndex = useMemo(() => {
    if (isPreview || isAnonymized || !isJobDetailsPage) return true;
    switch (jobPosition.status) {
      case JobPositionStatus.Paused:
        return jobPosition.jobTitles
          .flatMap(({ trackingPriority }) =>
            trackingPriority === CandidatePriority.D2 ? jobPosition.requiredPriorities : trackingPriority,
          )
          .filter(isDefined)
          .every(priority => !INITIATIVE_INDEXED_PRIORITIES.includes(priority as CandidatePriority));

      case JobPositionStatus.Published:
        return false;

      default:
        return true;
    }
  }, [isAnonymized, isJobDetailsPage, isPreview, jobPosition.jobTitles, jobPosition.requiredPriorities, jobPosition.status]);
  const isNoFollow = useMemo(
    () =>
      isJobRoute ? ![JobPositionStatus.Published, JobPositionStatus.Paused].includes(jobPosition.status as JobPositionStatus) : isNoIndex,
    [isJobRoute, jobPosition.status, isNoIndex],
  );

  return (
    <NextSeo
      description={description}
      openGraph={{
        locale: language || 'de_DE',
        type: 'basic',
        title,
        description,
        defaultImageHeight: 1200,
        defaultImageWidth: 630,
        url: canonical,
        images: [
          {
            url: getPublicImageUrl(jobPosition.employer.gallery?.[0]) || logoUrl,
          },
        ],
      }}
      title={title}
      titleTemplate='%s'
      flavorDomain={defaultDomain}
      defaultDomain={defaultDomain}
      noindex={isNoIndex}
      nofollow={isNoFollow}
      canonical={canonical}
    />
  );
};

/**
 * Gets the rotating title key based on the current week
 * Uses date-fns getWeek function for accurate week calculation
 * @returns The i18n key for the rotating title
 */
const getRotatingTitleKey = (): string => {
  const weekNumber = getWeek(new Date());
  const titleIndex = weekNumber % NUMBER_OF_ROTATING_TITLES;

  return `seo.rotatingTitles.${titleIndex}`;
};

export default JobDetailsSeoTags;
